import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, ScanLine, IndianRupee, Palette, Image as ImageIcon, Mail, Eye, EyeOff, Info } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

// Sprint 2026-07: Invoicing revamp. Adds branding (color + logo), UPI QR
// config, Razorpay BYOK payment links, and feature toggles. Sensitive values
// (Razorpay secret) are write-only — the API returns `razorpay_configured`
// as a boolean, not the actual secret.
const TEMPLATES = [
    { value: 'classic', label: 'Classic — bold header, structured layout' },
    { value: 'colored', label: 'Colored / Branded — accent color + logo prominent' },
];

const DEFAULT_FORM = {
    // Numbering & defaults
    prefix: 'INV',
    default_terms: '',
    default_notes: '',
    default_payment_terms_days: 30,
    default_template: 'classic',
    // Bank details
    bank_details: { bank_name: '', account_name: '', account_number: '', ifsc: '', upi_id: '' },
    // Branding
    brand_color: '',
    logo_url: '',
    // UPI QR
    upi_vpa: '',
    upi_display_name: '',
    enable_upi_qr: true,
    // Razorpay
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_configured: false,
    enable_razorpay_link: false,
    // Email
    enable_email_send: true,
};

export default function InvoiceSettingsPage() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [showSecret, setShowSecret] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const s = await api.invGetSettings();
                setForm({
                    ...DEFAULT_FORM,
                    ...s,
                    bank_details: { ...DEFAULT_FORM.bank_details, ...(s.bank_details || {}) },
                    // razorpay_key_secret never comes back from the API — start blank.
                    razorpay_key_secret: '',
                });
            } catch (e) {
                toast.error('Failed to load settings');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setBank = (k, v) => setForm((f) => ({ ...f, bank_details: { ...f.bank_details, [k]: v } }));

    const onSave = async () => {
        // Basic client-side sanity
        if (form.enable_upi_qr && form.upi_vpa && !/^[a-zA-Z0-9._-]{2,64}@[a-zA-Z]{2,32}$/.test(form.upi_vpa.trim())) {
            toast.error('UPI ID looks off — expected format is "name@handle" (e.g. 9876543210@ybl)');
            return;
        }
        if (form.enable_razorpay_link && !form.razorpay_key_id && !form.razorpay_configured) {
            toast.error('Add your Razorpay Key ID before enabling payment links');
            return;
        }
        // Prepare payload — only include razorpay_key_secret when the user
        // actually typed something (empty string means "keep existing").
        const payload = { ...form };
        if (!payload.razorpay_key_secret) delete payload.razorpay_key_secret;
        delete payload.razorpay_configured; // read-only
        setSaving(true);
        try {
            const updated = await api.invUpdateSettings(payload);
            setForm({ ...DEFAULT_FORM, ...updated, bank_details: { ...DEFAULT_FORM.bank_details, ...(updated.bank_details || {}) }, razorpay_key_secret: '' });
            toast.success('Settings saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Skeleton className="h-64 w-full" />;

    return (
        <div className="space-y-4 max-w-3xl pb-16" data-testid="invoice-settings-page">
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-back">
                    <ArrowLeft size={16} />
                </Button>
                <h1 className="text-2xl font-heading font-semibold tracking-tight">Invoice Settings</h1>
            </div>

            {/* ============ Numbering & Defaults ============ */}
            <Card>
                <CardHeader><CardTitle className="text-base">Numbering &amp; Defaults</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">Invoice prefix</Label>
                            <Input data-testid="input-prefix" value={form.prefix} onChange={(e) => setField('prefix', e.target.value.toUpperCase())} className="font-mono" />
                            <div className="text-xs text-muted-foreground mt-1">e.g. "INV" → INV/26-27/0001. Locked once you issue your first invoice.</div>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Default payment terms (days)</Label>
                            <Input data-testid="input-terms" type="number" min={0} max={365} value={form.default_payment_terms_days} onChange={(e) => setField('default_payment_terms_days', Number(e.target.value))} />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs text-muted-foreground">Default PDF template</Label>
                            <Select value={form.default_template} onValueChange={(v) => setField('default_template', v)}>
                                <SelectTrigger data-testid="select-template"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TEMPLATES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <div className="text-xs text-muted-foreground mt-1">You can override this per-invoice at issue time.</div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Default terms &amp; conditions</Label>
                        <Textarea data-testid="input-tnc" rows={4} value={form.default_terms} onChange={(e) => setField('default_terms', e.target.value)} placeholder={'e.g. 1. Payment due within 30 days.\n2. Interest @18% p.a. on overdue.'} />
                    </div>
                    <div>
                        <Label className="text-xs text-muted-foreground">Default notes to customer</Label>
                        <Textarea data-testid="input-def-notes" rows={2} value={form.default_notes} onChange={(e) => setField('default_notes', e.target.value)} />
                    </div>
                </CardContent>
            </Card>

            {/* ============ Branding (Sprint 2026-07) ============ */}
            <Card data-testid="card-branding">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Palette size={16} /> Branding
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">Brand color (hex)</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    data-testid="input-brand-color"
                                    value={form.brand_color || ''}
                                    onChange={(e) => setField('brand_color', e.target.value)}
                                    placeholder="#0F766E"
                                    className="font-mono"
                                />
                                <input
                                    type="color"
                                    value={form.brand_color || '#0F766E'}
                                    onChange={(e) => setField('brand_color', e.target.value.toUpperCase())}
                                    className="h-9 w-12 rounded border border-input cursor-pointer"
                                    data-testid="input-brand-color-picker"
                                    aria-label="Brand color picker"
                                />
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">Used on Colored template header, buttons &amp; accents.</div>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Logo URL</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    data-testid="input-logo-url"
                                    value={form.logo_url || ''}
                                    onChange={(e) => setField('logo_url', e.target.value)}
                                    placeholder="https://your-cdn.com/logo.png"
                                />
                                {form.logo_url ? (
                                    <div className="h-9 w-9 rounded border border-input flex items-center justify-center bg-muted overflow-hidden shrink-0">
                                        <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full" data-testid="logo-preview" />
                                    </div>
                                ) : (
                                    <div className="h-9 w-9 rounded border border-dashed border-input flex items-center justify-center text-muted-foreground shrink-0">
                                        <ImageIcon size={14} />
                                    </div>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">Any public HTTPS URL. Square logos look best. PNG with transparency preferred.</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ============ UPI QR (Sprint 2026-07) ============ */}
            <Card data-testid="card-upi">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <ScanLine size={16} /> UPI QR on invoice
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-medium">Show QR on PDF</div>
                            <div className="text-xs text-muted-foreground">Customers scan and the amount is pre-filled in their UPI app.</div>
                        </div>
                        <Switch checked={form.enable_upi_qr} onCheckedChange={(v) => setField('enable_upi_qr', v)} data-testid="switch-upi" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">UPI ID (VPA)</Label>
                            <Input
                                data-testid="input-upi-vpa"
                                value={form.upi_vpa || ''}
                                onChange={(e) => setField('upi_vpa', e.target.value.trim())}
                                placeholder="9876543210@ybl"
                                className="font-mono"
                                disabled={!form.enable_upi_qr}
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Display name (shown to payer)</Label>
                            <Input
                                data-testid="input-upi-name"
                                value={form.upi_display_name || ''}
                                onChange={(e) => setField('upi_display_name', e.target.value)}
                                placeholder="Your legal / trade name"
                                disabled={!form.enable_upi_qr}
                            />
                        </div>
                    </div>
                    <Alert>
                        <Info size={14} />
                        <AlertDescription className="text-xs">
                            The QR encodes <code className="bg-muted px-1 py-0.5 rounded">upi://pay?pa=&lt;VPA&gt;&amp;am=&lt;amount&gt;&amp;cu=INR</code> so any UPI app opens with the exact invoice amount pre-filled — no typing.
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>

            {/* ============ Razorpay Pay Links (Sprint 2026-07) ============ */}
            <Card data-testid="card-razorpay">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <IndianRupee size={16} /> Online payment link (Razorpay)
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-medium">Enable Razorpay Payment Links</div>
                            <div className="text-xs text-muted-foreground">Adds a "Pay Online" button to every issued invoice PDF.</div>
                        </div>
                        <Switch checked={form.enable_razorpay_link} onCheckedChange={(v) => setField('enable_razorpay_link', v)} data-testid="switch-razorpay" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">Key ID</Label>
                            <Input
                                data-testid="input-rzp-key"
                                value={form.razorpay_key_id || ''}
                                onChange={(e) => setField('razorpay_key_id', e.target.value.trim())}
                                placeholder="rzp_live_XXXXXXXXXXXXX"
                                className="font-mono"
                                disabled={!form.enable_razorpay_link}
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">
                                Key Secret
                                {form.razorpay_configured && !form.razorpay_key_secret && (
                                    <span className="ml-2 text-emerald-600">✓ saved</span>
                                )}
                            </Label>
                            <div className="relative">
                                <Input
                                    data-testid="input-rzp-secret"
                                    type={showSecret ? 'text' : 'password'}
                                    value={form.razorpay_key_secret || ''}
                                    onChange={(e) => setField('razorpay_key_secret', e.target.value.trim())}
                                    placeholder={form.razorpay_configured ? '••••••••  (leave blank to keep)' : 'Your Razorpay secret'}
                                    className="font-mono pr-10"
                                    disabled={!form.enable_razorpay_link}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowSecret((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    tabIndex={-1}
                                    aria-label="Toggle secret visibility"
                                >
                                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                            </div>
                        </div>
                    </div>
                    <Alert>
                        <Info size={14} />
                        <AlertDescription className="text-xs">
                            Get your keys from <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="underline">Razorpay Dashboard → Settings → API Keys</a>. Money from these payment links lands directly in <em>your</em> Razorpay account. Ease My Bill never sees or holds it.
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>

            {/* ============ Email delivery ============ */}
            <Card data-testid="card-email">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Mail size={16} /> Email invoices to customers
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-medium">Show "Send email" button on invoices</div>
                            <div className="text-xs text-muted-foreground">Uses your SMTP config from the main Settings page.</div>
                        </div>
                        <Switch checked={form.enable_email_send} onCheckedChange={(v) => setField('enable_email_send', v)} data-testid="switch-email" />
                    </div>
                </CardContent>
            </Card>

            {/* ============ Bank details ============ */}
            <Card>
                <CardHeader><CardTitle className="text-base">Bank details (also shown on invoice PDF)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-xs text-muted-foreground">Bank name</Label><Input data-testid="input-bank" value={form.bank_details.bank_name} onChange={(e) => setBank('bank_name', e.target.value)} /></div>
                        <div><Label className="text-xs text-muted-foreground">Account holder name</Label><Input data-testid="input-acc-name" value={form.bank_details.account_name} onChange={(e) => setBank('account_name', e.target.value)} /></div>
                        <div><Label className="text-xs text-muted-foreground">Account number</Label><Input data-testid="input-acc-no" value={form.bank_details.account_number} onChange={(e) => setBank('account_number', e.target.value)} className="font-mono" /></div>
                        <div><Label className="text-xs text-muted-foreground">IFSC</Label><Input data-testid="input-ifsc" value={form.bank_details.ifsc} onChange={(e) => setBank('ifsc', e.target.value.toUpperCase())} className="font-mono" /></div>
                    </div>
                </CardContent>
            </Card>

            <div className="flex items-center gap-2 pb-8">
                <Button onClick={onSave} disabled={saving} data-testid="btn-save">
                    <Save size={16} className="mr-1.5" /> {saving ? 'Saving…' : 'Save settings'}
                </Button>
                <Button variant="outline" onClick={() => nav(-1)} data-testid="btn-cancel">Cancel</Button>
            </div>
        </div>
    );
}
